import { ArrowLeft, CheckCircle, Loader2, Shield } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import { MobileInput } from '../common/MobileInput';
import { ui } from '../../styles/ui';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { handleRegister } = useAppState();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    role: 'Admin',
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile || !formData.password) {
        throw new Error('Please fill in all required fields');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }
      if (formData.mobile.length !== 10) {
        throw new Error('Please enter a valid 10-digit mobile number');
      }
      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const registerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobile: `+91${formData.mobile}`,
        password: formData.password,
        role: formData.role,
      };

      const result = await handleRegister(registerData);
      if (result.success) {
        setSuccessMessage('Registration successful! Redirecting to company setup...');
        setTimeout(() => navigate('/create-company'), 800);
      }
    } catch (err) {
      setSubmitError(err.message || 'Registration failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <button onClick={() => navigate('/login')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Login
        </button>

        <div className={`${ui.card} p-6`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-violet-600">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Create Account</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Register for Aegis Verify Admin Portal</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={ui.label}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={ui.input} placeholder="Enter first name" required disabled={submitLoading} />
              </div>
              <div>
                <label className={ui.label}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={ui.input} placeholder="Enter last name" required disabled={submitLoading} />
              </div>
            </div>

            <div>
              <label className={ui.label}>
                Email Address <span className="text-red-500">*</span>
              </label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={ui.input} placeholder="admin@barcodeportal.com" required disabled={submitLoading} />
            </div>

            <div>
              <label className={ui.label}>
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <MobileInput
                value={formData.mobile}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, mobile: v }));
                  if (submitError) setSubmitError('');
                  if (successMessage) setSuccessMessage('');
                }}
                disabled={submitLoading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={ui.label}>
                  Password <span className="text-red-500">*</span>
                </label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} className={ui.input} placeholder="Enter password" required disabled={submitLoading} />
              </div>
              <div>
                <label className={ui.label}>
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} className={ui.input} placeholder="Confirm password" required disabled={submitLoading} />
              </div>
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg text-sm text-red-700 dark:text-red-300">
                {submitError}
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-sm text-green-700 dark:text-green-300">{successMessage}</span>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button type="submit" disabled={submitLoading} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2">
                {submitLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4" />
                    Register
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
